import { useCallback, useEffect, useState } from 'react';
import { countBlanks, inferPick } from '@shared/rules';
import type { CardType } from '@shared/types';
import { TopBar, useDocumentTitle } from '../components/Chrome';
import { usePrefs } from '../components/Prefs';
import { admin, type AdminCard, type AdminPack, type AdminSummary, type Suggestion } from '../lib/api';
import { getAdminToken, saveAdminToken } from '../lib/storage';
import { extraAdminTabs } from './admin/tabs';

type Tab = string;
const BASE_TABS = ['suggestions', 'cards', 'packs', 'stats'];

export function AdminPage() {
  useDocumentTitle('Admin');
  const [authed, setAuthed] = useState(Boolean(getAdminToken()));
  const [tab, setTab] = useState<Tab>('suggestions');
  const [packs, setPacks] = useState<AdminPack[]>([]);
  const { toast } = usePrefs();

  const loadPacks = useCallback(async () => {
    try {
      setPacks(await admin.packs());
    } catch (e) {
      if (!getAdminToken()) setAuthed(false);
      else toast((e as Error).message, 'error');
    }
  }, [toast]);

  useEffect(() => {
    if (authed) void loadPacks();
  }, [authed, loadPacks]);

  if (!authed) return <Login onDone={() => setAuthed(true)} />;

  return (
    <>
      <TopBar>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => {
            saveAdminToken(null);
            setAuthed(false);
          }}
        >
          Log out
        </button>
      </TopBar>
      <main className="page wide">
        <div className="tabs" role="tablist">
          {BASE_TABS.map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
          {extraAdminTabs.map((t) => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'suggestions' && <Suggestions packs={packs} />}
        {tab === 'cards' && <Cards packs={packs} onChange={loadPacks} />}
        {tab === 'packs' && <Packs packs={packs} onChange={loadPacks} />}
        {tab === 'stats' && <Stats />}
        {extraAdminTabs.map((t) => (tab === t.key ? <t.component key={t.key} /> : null))}
      </main>
    </>
  );
}

function Login({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  return (
    <main className="page" style={{ paddingTop: '14vh', maxWidth: 420 }}>
      <form
        className="panel stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setError('');
          try {
            await admin.login(password);
            onDone();
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <h1>Admin</h1>
        <input className="input" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" aria-label="Password" />
        {error && <p className="error">{error}</p>}
        <button className="btn btn-primary">Log in</button>
      </form>
    </main>
  );
}

// ------------------------------------------------------------------ suggestions

function Suggestions({ packs }: { packs: AdminPack[] }) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [items, setItems] = useState<Suggestion[] | null>(null);
  const { toast } = usePrefs();
  const official = packs.filter((p) => p.kind === 'official');

  const load = useCallback(async () => {
    setItems(null);
    try {
      setItems(await admin.suggestions(status));
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }, [status, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="stack">
      <div className="segmented" style={{ maxWidth: 420 }}>
        {(['pending', 'approved', 'rejected'] as const).map((s) => (
          <button key={s} aria-pressed={status === s} onClick={() => setStatus(s)}>
            {s}
          </button>
        ))}
      </div>
      {!items && <p className="muted">Loading...</p>}
      {items?.length === 0 && <p className="muted">Nothing here.</p>}
      {items?.map((s) =>
        status === 'pending' ? (
          <SuggestionEditor key={s.id} s={s} packs={official} onDone={() => setItems((cur) => cur?.filter((x) => x.id !== s.id) ?? null)} />
        ) : (
          <div key={s.id} className="suggestion">
            <div className="row">
              <span className={`chip ${s.type === 'prompt' ? 'blue' : ''}`}>{s.type}</span>
              <strong>{s.text}</strong>
            </div>
            <span className="muted small">
              {s.packName ?? 'No pack'} · {s.submitterName || 'anonymous'} · {new Date(s.createdAt).toLocaleDateString()}
            </span>
          </div>
        ),
      )}
    </section>
  );
}

function SuggestionEditor({ s, packs, onDone }: { s: Suggestion; packs: AdminPack[]; onDone: () => void }) {
  const { toast } = usePrefs();
  const [type, setType] = useState<CardType>(s.type);
  const [text, setText] = useState(s.text);
  const [gloss, setGloss] = useState(s.gloss);
  const [explanation, setExplanation] = useState(s.explanation);
  const [packId, setPackId] = useState(s.packId ?? packs.find((p) => p.slug === 'classic')?.id ?? packs[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  // Packs load after this renders; fall back to Classic once they arrive.
  useEffect(() => {
    if (!packId && packs.length) setPackId(packs.find((p) => p.slug === 'classic')?.id ?? packs[0].id);
  }, [packs, packId]);
  const source = s.source === 'write_in' ? 'blank card in a game' : s.source === 'game' ? 'in-game menu' : 'website';

  async function approve() {
    setBusy(true);
    try {
      await admin.approve(s.id, { type, text, gloss, explanation, pick: type === 'prompt' ? inferPick(text) : 1, packId });
      toast('Added to the pack.', 'success');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="suggestion">
      <span className="muted small">
        From {s.submitterName || 'anonymous'} via {source}
        {s.roomCode ? ` (room ${s.roomCode})` : ''} · {new Date(s.createdAt).toLocaleString()}
      </span>
      <div className="grid-2">
        <select className="input" value={type} onChange={(e) => setType(e.target.value as CardType)} aria-label="Type">
          <option value="answer">Answer</option>
          <option value="prompt">Prompt</option>
        </select>
        <select className="input" value={packId} onChange={(e) => setPackId(e.target.value)} aria-label="Pack">
          {packs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <input className="input" value={text} onChange={(e) => setText(e.target.value)} aria-label="Text" />
      <div className="grid-2">
        <input className="input" value={gloss} onChange={(e) => setGloss(e.target.value)} placeholder="Translation" aria-label="Translation" />
        <input className="input" value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="Context" aria-label="Context" />
      </div>
      {type === 'prompt' && countBlanks(text.replace(/_{3,}/g, '___')) === 0 && <span className="muted small">No ___ blank: fine for a question, otherwise add one.</span>}
      <div className="row">
        <button className="btn btn-primary btn-sm" disabled={busy || !packId} onClick={approve}>
          Approve
        </button>
        <button
          className="btn btn-danger btn-sm"
          disabled={busy}
          onClick={async () => {
            await admin.reject(s.id).catch((e) => toast(e.message, 'error'));
            onDone();
          }}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ cards

function Cards({ packs, onChange }: { packs: AdminPack[]; onChange: () => void }) {
  const { toast } = usePrefs();
  const [packId, setPackId] = useState('');
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [cards, setCards] = useState<AdminCard[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!packId && packs.length) setPackId(packs[0].id);
  }, [packs, packId]);

  const load = useCallback(async () => {
    if (!packId) return;
    try {
      setCards(await admin.cards(packId, q, type));
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }, [packId, q, type, toast]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  async function exportCsv() {
    try {
      const csv = await admin.exportCsv(packId);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${packs.find((p) => p.id === packId)?.slug ?? 'pack'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }

  return (
    <section className="stack">
      <div className="row">
        <select className="input" style={{ maxWidth: 260 }} value={packId} onChange={(e) => setPackId(e.target.value)} aria-label="Pack">
          {packs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.kind === 'community' ? p.code : p.slug})
            </option>
          ))}
        </select>
        <select className="input" style={{ maxWidth: 140 }} value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All</option>
          <option value="prompt">Prompts</option>
          <option value="answer">Answers</option>
        </select>
        <input className="input grow" style={{ minWidth: 160 }} placeholder="Search text, translation, context" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="row">
        <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
          + Add card
        </button>
        <button className="btn btn-sm btn-outline" onClick={() => setImporting(true)}>
          Import CSV
        </button>
        <button className="btn btn-sm btn-outline" onClick={exportCsv}>
          Export CSV
        </button>
        <span className="muted small">{cards ? `${cards.length} cards` : ''}</span>
      </div>
      {adding && (
        <CardRowEditor
          card={{ id: '', packId, type: 'answer', text: '', pick: 1, gloss: '', explanation: '', active: true, plays: 0, wins: 0 }}
          onCancel={() => setAdding(false)}
          onSave={async (c) => {
            const type = c.type ?? 'answer';
            const text = c.text ?? '';
            await admin.addCard(packId, { type, text, gloss: c.gloss, explanation: c.explanation, pick: type === 'prompt' ? (c.pick ?? inferPick(text)) : 1 });
            setAdding(false);
            void load();
            onChange();
          }}
        />
      )}
      {importing && (
        <ImportCsv
          packId={packId}
          onDone={() => {
            setImporting(false);
            void load();
            onChange();
          }}
        />
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Card</th>
              <th>Plays</th>
              <th>Wins</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {cards?.map((c) => (
              <CardRow key={c.id} card={c} onChange={load} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CardRow({ card, onChange }: { card: AdminCard; onChange: () => void }) {
  const { toast } = usePrefs();
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <CardRowEditor
            card={card}
            onCancel={() => setEditing(false)}
            onSave={async (c) => {
              await admin.updateCard(card.id, c);
              setEditing(false);
              onChange();
            }}
            onDelete={async () => {
              if (!window.confirm('Delete this card?')) return;
              await admin.deleteCard(card.id).catch((e) => toast(e.message, 'error'));
              onChange();
            }}
          />
        </td>
      </tr>
    );
  }
  return (
    <tr className={card.active ? '' : 'inactive'}>
      <td>
        <span className={`chip ${card.type === 'prompt' ? 'blue' : ''}`}>
          {card.type}
          {card.type === 'prompt' && (card.pick ?? 1) > 1 ? ` ×${card.pick}` : ''}
        </span>
      </td>
      <td>
        <strong>{card.text}</strong>
        {card.gloss && <div className="muted small" style={{ fontStyle: 'italic' }}>{card.gloss}</div>}
        {card.explanation && <div className="muted small">ⓘ {card.explanation}</div>}
      </td>
      <td>{card.plays}</td>
      <td>{card.wins}</td>
      <td>
        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
          Edit
        </button>
      </td>
    </tr>
  );
}

function CardRowEditor({
  card,
  onSave,
  onCancel,
  onDelete,
}: {
  card: AdminCard;
  onSave: (c: Partial<AdminCard>) => Promise<void>;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const { toast } = usePrefs();
  const [c, setC] = useState(card);
  const [busy, setBusy] = useState(false);
  const blanks = countBlanks(c.text.replace(/_{3,}/g, '___'));
  return (
    <div className="stack panel" style={{ boxShadow: 'none' }}>
      <div className="row">
        <select className="input" style={{ maxWidth: 140 }} value={c.type} onChange={(e) => setC({ ...c, type: e.target.value as CardType })} aria-label="Type">
          <option value="answer">Answer</option>
          <option value="prompt">Prompt</option>
        </select>
        {c.type === 'prompt' && (
          <select className="input" style={{ maxWidth: 200 }} value={c.pick} onChange={(e) => setC({ ...c, pick: Number(e.target.value) })} aria-label="Pick">
            <option value={1}>Pick 1{blanks > 1 ? ' (same card in every blank)' : ''}</option>
            <option value={2}>Pick 2</option>
            <option value={3}>Pick 3</option>
          </select>
        )}
        <label className="check small">
          <input type="checkbox" checked={c.active} onChange={(e) => setC({ ...c, active: e.target.checked })} /> In play
        </label>
      </div>
      <input className="input" value={c.text} onChange={(e) => setC({ ...c, text: e.target.value })} placeholder="Card text" aria-label="Card text" />
      <input className="input" value={c.gloss ?? ''} onChange={(e) => setC({ ...c, gloss: e.target.value })} placeholder="Translation (shown on the card)" aria-label="Translation" />
      <textarea className="input" style={{ minHeight: 60 }} value={c.explanation ?? ''} onChange={(e) => setC({ ...c, explanation: e.target.value })} placeholder="Context (behind the i button)" aria-label="Context" />
      <div className="row">
        <button
          className="btn btn-sm btn-primary"
          disabled={busy || !c.text.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              await onSave({ type: c.type, text: c.text, pick: c.type === 'prompt' ? c.pick : 1, gloss: c.gloss, explanation: c.explanation, active: c.active });
            } catch (e) {
              toast((e as Error).message, 'error');
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
        <button className="btn btn-sm btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button className="btn btn-sm btn-danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

function ImportCsv({ packId, onDone }: { packId: string; onDone: () => void }) {
  const { toast } = usePrefs();
  const [csv, setCsv] = useState('');
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  return (
    <div className="panel stack">
      <strong>Import cards from CSV</strong>
      <span className="muted small">
        Columns: <code>type,text,pick,gloss,explanation</code>. type is prompt or answer; pick is optional.
      </span>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) setCsv(await file.text());
        }}
      />
      <textarea className="input" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="Or paste CSV here" />
      <div className="segmented" style={{ maxWidth: 360 }}>
        <button aria-pressed={mode === 'append'} onClick={() => setMode('append')}>
          Add to pack
        </button>
        <button aria-pressed={mode === 'replace'} onClick={() => setMode('replace')}>
          Replace all cards
        </button>
      </div>
      <div className="row">
        <button
          className="btn btn-primary btn-sm"
          disabled={!csv.trim()}
          onClick={async () => {
            if (mode === 'replace' && !window.confirm('Replace every card in this pack? Play stats for the old cards are lost.')) return;
            try {
              const r = await admin.importCsv(packId, csv, mode);
              toast(`Imported ${r.inserted} cards${r.skipped ? `, skipped ${r.skipped} duplicates` : ''}.`, 'success');
              onDone();
            } catch (e) {
              toast((e as Error).message, 'error');
            }
          }}
        >
          Import
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ packs

function Packs({ packs, onChange }: { packs: AdminPack[]; onChange: () => void }) {
  const { toast } = usePrefs();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  async function patch(id: string, p: Partial<AdminPack>) {
    try {
      await admin.updatePack(id, p);
      onChange();
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }

  return (
    <section className="stack">
      <form
        className="panel row"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await admin.createPack({ name, slug: slug || undefined });
            setName('');
            setSlug('');
            toast('Pack created. Add cards from the Cards tab.', 'success');
            onChange();
          } catch (err) {
            toast((err as Error).message, 'error');
          }
        }}
      >
        <input className="input grow" style={{ minWidth: 180 }} value={name} onChange={(e) => setName(e.target.value)} placeholder="New official pack name" required />
        <input className="input" style={{ maxWidth: 180 }} value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="slug (optional)" />
        <button className="btn btn-primary">Create</button>
      </form>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Pack</th>
              <th>Cards</th>
              <th>Live</th>
              <th>Default on</th>
              <th>18+</th>
              <th>Featured</th>
              <th>Order</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => (
              <tr key={p.id} className={p.active ? '' : 'inactive'}>
                <td>
                  <strong>{p.name}</strong>
                  <div className="muted small">
                    {p.kind === 'official' ? `official · ${p.slug ?? p.code}` : `community · ${p.code}${p.authorName ? ` · ${p.authorName}` : ''}`}
                  </div>
                </td>
                <td>
                  {p.promptCount}/{p.answerCount}
                </td>
                <td>
                  <input type="checkbox" checked={p.active} onChange={(e) => patch(p.id, { active: e.target.checked })} aria-label="Active" />
                </td>
                <td>
                  {p.kind === 'official' && <input type="checkbox" checked={p.isDefault} onChange={(e) => patch(p.id, { isDefault: e.target.checked })} aria-label="Default" />}
                </td>
                <td>
                  <input type="checkbox" checked={p.nsfw} onChange={(e) => patch(p.id, { nsfw: e.target.checked })} aria-label="18+" />
                </td>
                <td>
                  {p.kind === 'community' && <input type="checkbox" checked={p.featured} onChange={(e) => patch(p.id, { featured: e.target.checked })} aria-label="Featured" />}
                </td>
                <td>
                  <input
                    className="input"
                    style={{ width: 70, minHeight: 34, padding: '4px 8px' }}
                    type="number"
                    defaultValue={p.sortOrder}
                    onBlur={(e) => Number(e.target.value) !== p.sortOrder && patch(p.id, { sortOrder: Number(e.target.value) })}
                    aria-label="Sort order"
                  />
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={async () => {
                      if (!window.confirm(`Delete "${p.name}" and all its cards? This cannot be undone.`)) return;
                      await admin.deletePack(p.id).catch((e) => toast(e.message, 'error'));
                      onChange();
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        Featured community packs show up for every host. Unfeatured ones can only be added by code. Untick "Live" to hide a pack everywhere without deleting it.
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ stats

function Stats() {
  const [s, setS] = useState<AdminSummary | null>(null);
  const { toast } = usePrefs();
  useEffect(() => {
    admin.summary().then(setS).catch((e) => toast(e.message, 'error'));
  }, [toast]);
  if (!s) return <p className="muted">Loading...</p>;
  const max = Math.max(1, ...s.daily.map((d) => d.games));
  return (
    <section className="stack">
      <div className="stat-grid">
        <Stat n={s.live.roomCount} l="Live rooms" />
        <Stat n={s.live.connected} l="Players online" />
        <Stat n={s.totals.games7} l="Games, last 7 days" />
        <Stat n={s.totals.games30} l="Games, last 30 days" />
        <Stat n={s.totals.players30} l="Seats filled, 30 days" />
        <Stat n={s.pendingSuggestions} l="Suggestions waiting" />
      </div>
      <div className="panel">
        <h3>Games per day (last 14 days)</h3>
        <div className="bars">
          {s.daily.map((d) => (
            <div key={d.day} className="bar" style={{ height: `${(d.games / max) * 100}%` }} title={`${d.day}: ${d.games}`}>
              {d.games > 0 && <span>{d.games}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="grid-2">
        <div className="panel">
          <h3>Answers that win most</h3>
          <ol className="small">
            {s.topCards.map((c) => (
              <li key={c.id}>
                {c.text}{' '}
                <span className="muted">
                  ({c.wins}/{c.plays} · {c.pack_name})
                </span>
              </li>
            ))}
          </ol>
          {!s.topCards.length && <p className="muted small">Not enough games yet.</p>}
        </div>
        <div className="panel">
          <h3>Most played prompts</h3>
          <ol className="small">
            {s.topPrompts.map((c) => (
              <li key={c.id}>
                {c.text} <span className="muted">({c.plays})</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
      {s.live.rooms.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Room</th>
                <th>Phase</th>
                <th>Players</th>
                <th>Round</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {s.live.rooms.map((r) => (
                <tr key={r.code}>
                  <td>{r.code}</td>
                  <td>{r.phase}</td>
                  <td>
                    {r.online}/{r.players}
                  </td>
                  <td>{r.round}</td>
                  <td>{new Date(r.updatedAt).toLocaleTimeString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Stat({ n, l }: { n: number; l: string }) {
  return (
    <div className="stat">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
    </div>
  );
}
